// ============================================================
// lineFailure - did a request fail on the LINE, or did the server say no?
//
// A refusal is a fact about the session; a line failure is a fact
// about the next few seconds. Acting on the second as if it were the
// first threw players out of a session when their network dropped:
// the re-seat's rejoin failed with PocketBase's status 0 and the app
// read it as "the server refused you" (2026-10-02). Pure: the caller
// supplies the adapter's `errorKind` for the error.
// ============================================================
import type { BackendErrorKind } from '../backend/types';

/**
 * What browsers say when `fetch` never got an answer: Chromium, Firefox,
 * Safari, and the stream-read failure of a reply cut off mid-body.
 */
const FETCH_FAILURE =
  /^(Failed to fetch|NetworkError when attempting to fetch resource\.?|Load failed|network error)$/i;

/** A rejection the browser raised before any server answered, unclassified by an adapter. */
function isUnansweredFetch(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const { name, message } = err as { name?: unknown; message?: unknown };
  if (name === 'AbortError') return true;
  return name === 'TypeError' && typeof message === 'string' && FETCH_FAILURE.test(message);
}

/**
 * True when the request failed on its way - and so a later attempt may
 * well succeed - rather than by the server's answer.
 *
 * - `transient`: no answer at all (PocketBase status 0: offline, DNS, a
 *   reset, a cut-off reply, an abort) or a 5xx; Firestore's `unavailable`,
 *   `deadline-exceeded` and `aborted`.
 * - `throttled`: a 429 or `resource-exhausted`. The rate limiter behind
 *   one proxy is shared by the whole table (gotchas.md): a pause, not a no.
 * - a raw `fetch` failure no adapter wrapped.
 *
 * Everything else is an answer - `denied`, `missing`, `unauthenticated`,
 * the adapter's own "you are no longer a member" - and is NOT the line.
 */
export function isLineFailure(err: unknown, kind: BackendErrorKind): boolean {
  if (kind === 'transient' || kind === 'throttled') return true;
  return isUnansweredFetch(err);
}
